/**
 * WebhookDiagnosticsPanel — M2.3.3B · Diagnostic UI per Super Admin.
 *
 * Riusa gli endpoint READ-ONLY M2.2.6/M2.3.3 già deployati:
 *   - GET /api/admin/messaging/webhooks/recent
 *   - GET /api/admin/messaging/webhooks/messages-detail
 *
 * Usa l'autenticazione Super Admin già attiva nel browser (cookie httpOnly
 * o bearer sb_token via api.js). NESSUNA modifica lato DB/webhook/parser.
 *
 * NB: mostra sempre wa_id mascherato (`xxx***xx`) — coerente con i wrapper
 * degli endpoint diagnostici backend (che comunque restituiscono la shape
 * completa: la sanificazione visuale è qui aggiuntiva a tutela PII).
 */
import React, { useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function maskWa(w) {
  if (!w) return "";
  const s = String(w).replace(/^\+/, "");
  if (s.length < 6) return "***";
  return s.slice(0, 3) + "***" + s.slice(-2);
}

function KnownField(f) {
  return ["messages", "smb_message_echoes", "smb_app_state_sync",
           "history", "account_update"].includes(f);
}

const VALENTINA_TEST = {
  label: "Analizza test Valentina · 09/09/2026 12:48 UTC (Business App read only)",
  since: "2026-09-09T12:47:30Z",
  until: "2026-09-09T12:50:30Z",
};

export function WebhookDiagnosticsPanel() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [payload, setPayload] = useState(null);
  const [convStateInput, setConvStateInput] = useState("");
  const [customSince, setCustomSince] = useState(VALENTINA_TEST.since);
  const [customUntil, setCustomUntil] = useState(VALENTINA_TEST.until);
  const [waSuffix, setWaSuffix] = useState("");

  const runDiag = useCallback(async (opts) => {
    setLoading(true); setErr(null); setPayload(null);
    const since = opts.since; const until = opts.until;
    try {
      // Chiama entrambi gli endpoint in parallelo
      const [rRecent, rDetail] = await Promise.all([
        api.get("/admin/messaging/webhooks/recent",
                 { params: { limit: 500 } }),
        api.get("/admin/messaging/webhooks/messages-detail",
                 { params: {
                     since_iso: since, until_iso: until,
                     limit: 100,
                     ...(opts.conversation_id ? { conversation_id: opts.conversation_id } : {}),
                     ...(opts.wa_id_suffix ? { wa_id_suffix: opts.wa_id_suffix } : {}),
                 }}),
      ]);
      // Filtra recent alla finestra
      const inWindow = (rRecent.data?.items || []).filter((it) => {
        const r = it.received_at || "";
        return r >= since && r <= until;
      });
      // Aggregate per field nella finestra
      const fieldsAgg = {};
      const unknownFields = [];
      for (const it of inWindow) {
        const f = it.field || "unknown";
        fieldsAgg[f] = (fieldsAgg[f] || 0) + 1;
        if (!KnownField(f) && !unknownFields.includes(f)) unknownFields.push(f);
      }
      // Cerca eventi business-read candidates
      const businessReadCandidates = [];
      for (const it of inWindow) {
        const f = it.field;
        const s = it.shape || {};
        // Segnale: smb_app_state_sync con types diversi da 'contact'
        if (f === "smb_app_state_sync") {
          const types = s.state_sync_types || [];
          if (types.some((t) => t && t !== "contact")) {
            businessReadCandidates.push({ ...it, why: `state_sync_types=${JSON.stringify(types)}` });
          }
        }
        // Segnale: field sconosciuto
        if (!KnownField(f)) {
          businessReadCandidates.push({ ...it, why: `unknown_field=${f}` });
        }
      }
      const detailItems = rDetail.data?.items || [];
      // Un evento in messages-detail con id_meta che NON corrisponde a un
      // outbound Sarabi potrebbe suggerire uno "statuses.read" anomalo.
      const suspiciousStatuses = detailItems.filter((it) => {
        const msg = it.message_item_FULL || {};
        return it.field === "messages" &&
               msg.status === "read" &&
               it.persisted_in_whatsapp_messages &&
               it.persisted_in_whatsapp_messages.direction !== "out";
      });

      setPayload({
        window: { since, until },
        aggregate: { fields_in_window: fieldsAgg, unknown_fields: unknownFields },
        conversation_state: rDetail.data?.conversation_state,
        resolved_wa_id: rDetail.data?.resolved_wa_id,
        recent_in_window: inWindow,
        detail_items: detailItems,
        comparison: rDetail.data?.comparison,
        business_read_candidates: businessReadCandidates,
        suspicious_statuses: suspiciousStatuses,
      });
    } catch (e) {
      setErr(formatApiError(e) || "Errore");
    } finally {
      setLoading(false);
    }
  }, []);

  if (!isSuper) return null;

  const conclusion = (() => {
    if (!payload) return null;
    const found = payload.business_read_candidates.length > 0
      || payload.suspicious_statuses.length > 0;
    if (found) {
      return {
        text: "⚠️ Trovati eventi potenzialmente riconducibili a business-side read (vedi dettaglio). Analisi manuale richiesta prima di implementare M2.3.4.",
        color: "amber",
      };
    }
    // Nessun candidato
    return {
      text: "✅ Nessun evento business-side read trovato nella finestra. Meta non ha inviato segnali affidabili di lettura dalla WhatsApp Business App per il caso analizzato.",
      color: "emerald",
    };
  })();

  return (
    <section data-testid="webhook-diagnostics-panel"
             className="mt-8 rounded-3xl border-2 border-amber-300 bg-amber-50/50 p-6">
      <h2 className="font-serif text-2xl mb-2">
        Diagnostica webhook · Super Admin
      </h2>
      <p className="text-xs text-ink-500 mb-4">
        READ-ONLY. Nessuna modifica al DB, ai webhook o ai parser.
        Usa la tua autenticazione attuale.
      </p>

      <div className="flex flex-col gap-3 mb-4">
        <button
          type="button"
          data-testid="diag-valentina-btn"
          disabled={loading}
          onClick={() => runDiag({
            since: VALENTINA_TEST.since,
            until: VALENTINA_TEST.until,
          })}
          className="btn-primary self-start disabled:opacity-40"
        >
          {loading ? "Analisi in corso…" : VALENTINA_TEST.label}
        </button>

        <details className="rounded-2xl bg-white border border-sand-300 p-3">
          <summary className="cursor-pointer text-sm text-ink-700">
            Analisi custom (finestra + wa_id opzionale)
          </summary>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs">
              Since ISO (UTC)
              <input data-testid="diag-since-input"
                     value={customSince} onChange={(e) => setCustomSince(e.target.value)}
                     className="w-full mt-1 rounded-lg border border-sand-300 px-2 py-1 text-sm" />
            </label>
            <label className="text-xs">
              Until ISO (UTC)
              <input data-testid="diag-until-input"
                     value={customUntil} onChange={(e) => setCustomUntil(e.target.value)}
                     className="w-full mt-1 rounded-lg border border-sand-300 px-2 py-1 text-sm" />
            </label>
            <label className="text-xs md:col-span-2">
              wa_id_suffix (opzionale)
              <input data-testid="diag-wa-input"
                     value={waSuffix} onChange={(e) => setWaSuffix(e.target.value)}
                     placeholder="es. 25"
                     className="w-full mt-1 rounded-lg border border-sand-300 px-2 py-1 text-sm" />
            </label>
            <label className="text-xs md:col-span-2">
              conversation_id (opzionale, prevale su wa_id_suffix)
              <input data-testid="diag-conv-input"
                     value={convStateInput}
                     onChange={(e) => setConvStateInput(e.target.value)}
                     placeholder="es. 6aa111cf5af68c4afbf156da"
                     className="w-full mt-1 rounded-lg border border-sand-300 px-2 py-1 text-sm" />
            </label>
            <button
              type="button"
              data-testid="diag-custom-btn"
              disabled={loading}
              onClick={() => runDiag({
                since: customSince, until: customUntil,
                wa_id_suffix: waSuffix || undefined,
                conversation_id: convStateInput || undefined,
              })}
              className="btn-primary md:col-span-2 disabled:opacity-40"
            >
              Esegui diagnostica custom
            </button>
          </div>
        </details>
      </div>

      {err && (
        <div data-testid="diag-error"
             className="mb-3 rounded-2xl bg-red-50 border-2 border-red-200 p-3 text-sm text-red-800">
          {err}
        </div>
      )}

      {payload && (
        <div data-testid="diag-result" className="space-y-4">
          {conclusion && (
            <div data-testid="diag-conclusion"
                 className={`rounded-2xl p-4 border-2 ${
                   conclusion.color === "emerald"
                     ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                     : "bg-amber-100 border-amber-400 text-amber-900"
                 }`}>
              <div className="font-semibold text-sm">{conclusion.text}</div>
            </div>
          )}

          <div className="rounded-2xl bg-white border border-sand-300 p-3">
            <div className="text-xs uppercase tracking-wide text-ink-500 mb-2">
              Finestra
            </div>
            <div className="font-mono text-xs">
              {payload.window.since} → {payload.window.until}
            </div>
            {payload.resolved_wa_id && (
              <div className="mt-1 font-mono text-xs">
                wa_id: {maskWa(payload.resolved_wa_id)}
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white border border-sand-300 p-3">
            <div className="text-xs uppercase tracking-wide text-ink-500 mb-2">
              Aggregate campi nella finestra
            </div>
            <table className="text-xs w-full">
              <tbody>
                {Object.entries(payload.aggregate.fields_in_window).map(([f, n]) => (
                  <tr key={f} data-testid={`diag-field-${f}`}
                      className={KnownField(f) ? "" : "bg-amber-50"}>
                    <td className="py-0.5 pr-3 font-mono">{f}</td>
                    <td className="py-0.5">{n}</td>
                    <td className="py-0.5 text-ink-400">
                      {KnownField(f) ? "known" : "⚠️ unknown"}
                    </td>
                  </tr>
                ))}
                {Object.keys(payload.aggregate.fields_in_window).length === 0 && (
                  <tr><td colSpan={3} className="text-ink-500 py-2">
                    Nessun evento nella finestra.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {payload.conversation_state && (
            <div className="rounded-2xl bg-white border border-sand-300 p-3">
              <div className="text-xs uppercase tracking-wide text-ink-500 mb-2">
                Stato conversation (snapshot corrente)
              </div>
              <ul className="text-xs font-mono space-y-0.5">
                <li>id: {payload.conversation_state.id}</li>
                <li>wa_id: {maskWa(payload.conversation_state.wa_id)}</li>
                <li>unread_count: <b>{payload.conversation_state.unread_count}</b></li>
                <li>last_message_id_meta: {payload.conversation_state.last_message_id_meta || "—"}</li>
                <li>last_message_type: {payload.conversation_state.last_message_type || "—"}</li>
                <li>last_direction: {payload.conversation_state.last_direction || "—"}</li>
                <li>last_message_at: {payload.conversation_state.last_message_at || "—"}</li>
              </ul>
            </div>
          )}

          <div className="rounded-2xl bg-white border border-sand-300 p-3">
            <div className="text-xs uppercase tracking-wide text-ink-500 mb-2">
              Eventi raw nella finestra ({payload.recent_in_window.length})
            </div>
            <div className="max-h-[300px] overflow-y-auto text-xs font-mono space-y-1">
              {payload.recent_in_window.map((it, i) => (
                <div key={i} className={`p-2 rounded ${KnownField(it.field) ? "bg-sand-50" : "bg-amber-100"}`}>
                  <div>[{it.received_at}] field=<b>{it.field}</b></div>
                  <div className="text-ink-500">
                    keys={JSON.stringify((it.shape || {}).keys || [])}
                    {(it.shape || {}).state_sync_types && (
                      <> · state_sync_types={JSON.stringify(it.shape.state_sync_types)}</>
                    )}
                    {(it.shape || {}).state_sync_actions && (
                      <> · state_sync_actions={JSON.stringify(it.shape.state_sync_actions)}</>
                    )}
                    {(it.shape || {}).statuses_types && (
                      <> · statuses={JSON.stringify(it.shape.statuses_types)}</>
                    )}
                  </div>
                  {/* P0-6 Fase 2A · Step 1: dettaglio per-item state_sync (NON-PII) */}
                  {Array.isArray((it.shape || {}).state_sync_items) &&
                   it.shape.state_sync_items.length > 0 && (
                    <div className="mt-1 text-ink-600 pl-3 border-l-2 border-sand-300">
                      state_sync_items:
                      <ul className="ml-3 space-y-0.5">
                        {it.shape.state_sync_items.map((item, j) => (
                          <li key={j} data-testid={`state-sync-item-${i}-${j}`}>
                            {"{"}
                            type={JSON.stringify(item.type)},{" "}
                            action={JSON.stringify(item.action)},{" "}
                            ts={item.metadata_timestamp !== null && item.metadata_timestamp !== undefined
                                ? JSON.stringify(item.metadata_timestamp)
                                : "null"},{" "}
                            ver={item.metadata_version !== null && item.metadata_version !== undefined
                                ? JSON.stringify(item.metadata_version)
                                : "null"},{" "}
                            hash={item.contact_hash
                                ? `"${item.contact_hash}"`
                                : "null"}
                            {Array.isArray(item.contact_keys) && (
                              <>
                                ,{" "}
                                <span data-testid={`state-sync-item-${i}-${j}-contact-keys`}>
                                  contact_keys={JSON.stringify(item.contact_keys)}
                                </span>
                              </>
                            )}
                            {"}"}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
              {payload.recent_in_window.length === 0 && (
                <div className="text-ink-500">Nessun evento raw.</div>
              )}
            </div>
          </div>

          {payload.detail_items.length > 0 && (
            <div className="rounded-2xl bg-white border border-sand-300 p-3">
              <div className="text-xs uppercase tracking-wide text-ink-500 mb-2">
                Detail messaggi ({payload.detail_items.length})
              </div>
              <div className="max-h-[300px] overflow-y-auto text-xs font-mono space-y-2">
                {payload.detail_items.map((it, i) => {
                  const msg = it.message_item_FULL || {};
                  return (
                    <div key={i} className="p-2 rounded bg-sand-50">
                      <div>[{it.received_at}] field={it.field}</div>
                      <div>id_meta: {msg.id || "—"}</div>
                      <div>type: <b>{msg.type || "—"}</b> · from: {maskWa(msg.from)} · to: {maskWa(msg.to)}</div>
                      <div>ts: {msg.timestamp}</div>
                      {msg.status && <div>status: {msg.status}</div>}
                      {it.persisted_in_whatsapp_messages && (
                        <div className="text-ink-500">
                          persisted.type={it.persisted_in_whatsapp_messages.type}
                          {" · direction="}{it.persisted_in_whatsapp_messages.direction}
                          {" · raw_matches="}
                          {String(it.persisted_in_whatsapp_messages.raw_event_matches_this_webhook)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {payload.comparison && (
            <div className="rounded-2xl bg-white border border-sand-300 p-3">
              <div className="text-xs uppercase tracking-wide text-ink-500 mb-2">
                Comparison (primi 2 items)
              </div>
              <pre className="text-xs">{JSON.stringify(payload.comparison, null, 2)}</pre>
            </div>
          )}

          {payload.business_read_candidates.length > 0 && (
            <div data-testid="diag-candidates"
                 className="rounded-2xl bg-amber-100 border-2 border-amber-400 p-3">
              <div className="text-xs uppercase tracking-wide text-amber-900 mb-2 font-semibold">
                ⚠️ Candidati business-read ({payload.business_read_candidates.length})
              </div>
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(payload.business_read_candidates, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
